// Object pronoun topic: session building + register-aware cluster matching.
// Pure (no React) so the accepted-answer logic can be unit-tested on its own.
//
// One drill format: build the verb + pronoun. The sentence keeps its trigger
// words and context visible (placement depends on them: não, já, que, question
// words) and the blank is the verb + pronoun cluster, which is what people
// produce together in real speech. The cue names the base verb plus the object:
// the noun for a third-person object (person or thing), the pronoun for
// me/te/nos. Every third-person cue names its object, so nothing in the prompt
// reveals whether the answer drops: the learner applies the rule each time —
// a person takes a pronoun, a thing is dropped in casual speech.
//
// Grading looks only at the cluster, in the register the learner picked:
//
//  • formal — prescriptive placement and form (enclisis "comprei-o", proclisis
//    "não o vi" after a trigger). Only the formal forms pass.
//  • casual — the natural equivalent: ele/ela after the verb ("encontrei ela",
//    "pra ele"), or the verb alone for a thing ("comprei"). The formal form
//    does NOT pass, but is shown in the feedback as the contrast.
//
// me/te/nos behave the same in both registers, so for those items every listed
// form passes whichever register is chosen.

import data from '../../data/objectPronouns.json'
import { shuffle } from './phraseBank'

export type Register = 'written' | 'spoken'
export type OPCategory = 'direct' | 'indirect' | 'reflexive'

// The written-Portuguese position of the pronoun relative to the verb. This is
// the axis the learner actually picks; `ProclisisTrigger` is the finer-grained
// reason, kept on each item as descriptive metadata.
export type Placement = 'enclisis' | 'proclisis'
export type ProclisisTrigger =
  | 'none'
  | 'negation'
  | 'adverb'
  | 'conjunction'
  | 'question-word'
  | 'subordinate'
export const PROCLISIS_TRIGGER_IDS: ProclisisTrigger[] = [
  'none',
  'negation',
  'adverb',
  'conjunction',
  'question-word',
  'subordinate',
]

export function placementOf(trigger: ProclisisTrigger): Placement {
  return trigger === 'none' ? 'enclisis' : 'proclisis'
}

export type OPCategoryMeta = {
  id: OPCategory
  label: string
  // What the pronoun does, shown in the setup chip: the categories sort prompts by
  // function, since the forms (me, te, nos) overlap across them.
  description: string
  defaultOn: boolean
}

// What the object is. "person" takes a pronoun (ele/ela) in casual speech,
// "thing" is dropped, "pronoun" is a reflexive me/te/nos/se.
export type ObjectKind = 'person' | 'thing' | 'pronoun'

export type OPItem = {
  id: string
  category: OPCategory
  trigger: ProclisisTrigger
  // The one line that sets up the situation, if any ("Você comprou o pão?"). It may
  // name the object; there is no separate scene-setting sentence.
  question: string | null
  // English meaning shown as the prompt when the sentence is too short to leave
  // an anchor word beside the blank ("Help me.").
  say: string | null
  // The words either side of the blank; `after` may start with punctuation that
  // hugs the cue (", por favor." / "?").
  before: string
  after: string
  // The cue: the base verb + the object (noun, or me/te/nos/se).
  verb: string
  object: string
  kind: ObjectKind
  // Accepted clusters. formal[0] / casual[0] is the one shown and read aloud.
  formal: string[]
  casual: string[]
  // The full sentence in English, shown after the answer.
  translation: string
}

export const OP_CATEGORIES = data.categories as OPCategoryMeta[]
export const OP_ITEMS = data.items as OPItem[]
export const DEFAULT_OP_CATEGORY_IDS = OP_CATEGORIES.filter((category) => category.defaultOn).map(
  (category) => category.id,
)

// "casual" is everyday talk AND casual writing (texts, chat); "formal" is
// edited/formal writing AND formal speech. Casual first — the default, and what
// a conversational learner practises most. (The `id`s stay spoken/written.)
export const OP_REGISTERS: Array<{ id: Register; label: string }> = [
  { id: 'spoken', label: 'Casual' },
  { id: 'written', label: 'Formal' },
]

// Formal-mode only: which side of the verb the pronoun sits, with the cases
// that put it there listed the way the contraction drill lists its sub-types.
export const OP_PLACEMENTS: Array<{ id: Placement; label: string; cases: string }> = [
  { id: 'enclisis', label: 'After the verb', cases: 'default, with exception of before the verb scenarios' },
  {
    id: 'proclisis',
    label: 'Before the verb',
    cases: 'negation · adverb (já, sempre) · conjunction (que, porque) · question word · subordinate clause',
  },
]
export const DEFAULT_OP_PLACEMENT_IDS: Placement[] = OP_PLACEMENTS.map((placement) => placement.id)

export const OP_SESSION_LENGTH = 10

// Compare replies leniently on trailing punctuation and internal spacing, but
// keep it case-fold + accent-exact like every other drill in the app.
export function normalizeReply(value: string): string {
  return value
    .normalize('NFC')
    .toLocaleLowerCase('pt')
    .trim()
    .replace(/[.!?,;:]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

// "pra" and "para" are the same word out loud, so a casual cluster written with
// one is accepted with the other.
function withParaVariants(answers: string[]): string[] {
  return answers.flatMap((answer) => (/\bpra\b/.test(answer) ? [answer, answer.replace(/\bpra\b/g, 'para')] : [answer]))
}

// The set of answers that count as correct for one item in one register.
// me/te/nos items are register-neutral, so both lists pass in either mode.
export function acceptedReplies(item: OPItem, register: Register): string[] {
  if (item.kind === 'pronoun') return [...new Set([...item.formal, ...item.casual])]
  return register === 'written' ? item.formal : withParaVariants(item.casual)
}

// The cue shown in parentheses after the blank: "encontrar + a Maria".
export function cueOf(item: OPItem): string {
  return `${item.verb} + ${item.object}`
}

const wordsOf = (text: string): string[] => text.split(/\s+/).filter(Boolean)
const HUG = /^[.!?,;:]+/

// The words around the blank, split the way the drill card lays them out:
// punctuation that hugs the cue, then the plain words after it.
export function sentenceParts(item: OPItem): { beforeWords: string[]; afterWords: string[]; suffix: string } {
  const suffix = item.after.match(HUG)?.[0] ?? ''
  return { beforeWords: wordsOf(item.before), afterWords: wordsOf(item.after.slice(suffix.length)), suffix }
}

function capitalized(text: string): string {
  return text.charAt(0).toLocaleUpperCase('pt') + text.slice(1)
}

// The completed sentence for a given cluster, for reading aloud and the
// correct-answer render.
export function sentenceWith(item: OPItem, cluster: string): string {
  const { beforeWords, afterWords, suffix } = sentenceParts(item)
  return capitalized(`${[...beforeWords, `${cluster}${suffix}`, ...afterWords].join(' ')}`)
}

// Grading looks at the cluster only. A learner who types the whole sentence
// gets the words around the blank stripped off before the comparison.
export function clusterOf(item: OPItem, value: string): string {
  let reply = normalizeReply(value)
  const before = normalizeReply(item.before)
  if (before && reply.startsWith(`${before} `)) reply = reply.slice(before.length + 1)
  const after = normalizeReply(item.after.replace(HUG, ''))
  if (after && reply.endsWith(` ${after}`)) reply = reply.slice(0, -(after.length + 1))
  return reply.trim()
}

export function replyIsCorrect(item: OPItem, register: Register, value: string): boolean {
  const target = clusterOf(item, value)
  return acceptedReplies(item, register).some((answer) => normalizeReply(answer) === target)
}

// Items available for the chosen categories + register. The placement filter
// only applies in written mode.
export function opPool(
  categoryIds: OPCategory[],
  register: Register,
  placementIds: Placement[],
): OPItem[] {
  return OP_ITEMS.filter((item) => {
    if (!categoryIds.includes(item.category)) return false
    if (register === 'written' && !placementIds.includes(placementOf(item.trigger))) return false
    return true
  })
}

export type OPPrompt = {
  item: OPItem
  register: Register
  // Lines above the answer line: the question being answered, or the English
  // "say: …" when the sentence is too short to anchor the blank.
  promptLines: string[]
  cue: string
  beforeWords: string[]
  afterWords: string[]
  suffix: string
  // Canonical cluster for the chosen register (shown on a miss).
  canonicalAnswer: string
  acceptedAnswers: string[]
  contrast: { written: string; spoken: string; active: Register }
}

export function promptFor(item: OPItem, register: Register): OPPrompt {
  return {
    item,
    register,
    promptLines: [item.question, item.say ? `say: ${item.say}` : null].filter(
      (line): line is string => Boolean(line),
    ),
    cue: cueOf(item),
    ...sentenceParts(item),
    canonicalAnswer: register === 'written' ? item.formal[0] : item.casual[0],
    acceptedAnswers: acceptedReplies(item, register),
    contrast: { written: item.formal[0], spoken: item.casual[0], active: register },
  }
}

export function buildObjectPronounSession(
  categoryIds: OPCategory[],
  register: Register,
  placementIds: Placement[],
): OPPrompt[] {
  return shuffle(opPool(categoryIds, register, placementIds))
    .slice(0, OP_SESSION_LENGTH)
    .map((item) => promptFor(item, register))
}
